import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Pencil, CircleCheck, RefreshCw, Loader2, ChevronDown } from "lucide-react";
import { ProfileResponse } from '../../../api/types';
import settingsApi from '../../../api/settingsApi';
import authApi from '../../../api/authApi';
import vendorApi from '../../../api/vendorApi';
import { useWorkspace } from '../../../context/WorkspaceContext';
import workspaceApi from '../../../api/workspaceApi';

import { contactUpdateSchema } from '../../../utils/inputSchema'

interface ProfileProps {
    data: ProfileResponse;
    handleSaveEdit: () => void;
}

const TIMEZONE_OPTIONS = [
    { value: 'America/New_York', label: 'ET - New York' },
    { value: 'America/Chicago', label: 'CT - Chicago' },
    { value: 'America/Denver', label: 'MT - Denver' },
    { value: 'America/Phoenix', label: 'MST - Phoenix' },
    { value: 'America/Los_Angeles', label: 'PT - Los Angeles' },
    { value: 'America/Anchorage', label: 'AKT - Anchorage' },
    { value: 'Pacific/Honolulu', label: 'HST - Honolulu' },
    { value: 'America/Toronto', label: 'ET - Toronto' },
    { value: 'America/Vancouver', label: 'PT - Vancouver' },
    { value: 'Europe/London', label: 'GMT - London' },
    { value: 'Europe/Paris', label: 'CET - Paris' },
    { value: 'Asia/Tokyo', label: 'JST - Tokyo' },
    { value: 'Asia/Taipei', label: 'CST - Taipei' },
    { value: 'Australia/Sydney', label: 'AET - Sydney' },
];

export default function Profile({ data, handleSaveEdit }: ProfileProps) {
    const [editingPersonal, setEditingPersonal] = useState(false);
    const [editingBusiness, setEditingBusiness] = useState(false);
    const [errors, setErrors] = useState<{ new_email?: string; new_phone?: string }>({});
    const [apiError, setApiError] = useState<string | null>(null);
    const [isRenormalizing, setIsRenormalizing] = useState(false);
    const [isSyncingQbo, setIsSyncingQbo] = useState(false);
    const [isSavingTimezone, setIsSavingTimezone] = useState(false);
    const [timezoneError, setTimezoneError] = useState<string | null>(null);
    const { workspaces, activeWorkspaceId, setActiveWorkspaceId, businesses, activeBusinessId, setActiveBusinessId, refetch: refetchWorkspaceContext } = useWorkspace();
    const activeWorkspace = workspaces.find(ws => ws.workspace_id === activeWorkspaceId);
    const [selectedTimezone, setSelectedTimezone] = useState(activeWorkspace?.timezone || 'America/New_York');

    const { data: industries } = useQuery({
        queryKey: ['industries'],
        queryFn: vendorApi.getIndustries,
    });

    const navigate = useNavigate();

    useEffect(() => {
        setSelectedTimezone(activeWorkspace?.timezone || 'America/New_York');
    }, [activeWorkspace?.timezone, activeWorkspaceId]);

    const [contactForm, setContactForm] = useState({
        new_email: '',
        new_phone: '',
    });


    const [businessForm, setBusinessForm] = useState(() => {
        const bd = data?.business_details ?? {};
        return {
            business_name: bd.business_name ?? '',
            business_entity_name: bd.business_entity_name ?? '',
            employer_identification_number: bd.employer_identification_number ?? '',
            industry: bd.industry ?? '',
            business_type: bd.business_type ?? '',
            date_business_formed: bd.date_business_formed ?? '',
            street_address_1: bd.street_address_1 ?? '',
            street_address_2: bd.street_address_2 ?? '',
            city: bd.city ?? '',
            state: bd.state ?? '',
            country: bd.country ?? '',
            zipcode: bd.zipcode ?? '',
        };
    });


    const initials = `${data.personal_details.first_name[0]}${data.personal_details.last_name[0]}`;

    const handleContactSave = async () => {
        try {
            setApiError(null);

            // 1. Validate input
            const sanitizedInput = {
                new_email: contactForm.new_email.trim() || undefined,
                new_phone: contactForm.new_phone.trim() || undefined,
            };
            await contactUpdateSchema.validate(sanitizedInput, { abortEarly: false });
            setErrors({});

            const emailChanged = sanitizedInput.new_email && sanitizedInput.new_email !== data.personal_details.email;
            const phoneChanged = sanitizedInput.new_phone && sanitizedInput.new_phone !== data.personal_details.phone;

            // 2. If neither changed, just exit
            if (!emailChanged && !phoneChanged) {
                setEditingPersonal(false);
                return;
            }

            // 3. Save updates to profile first
            await settingsApi.updateContactInfo({
                new_email: emailChanged ? sanitizedInput.new_email : undefined,
                new_phone: phoneChanged ? sanitizedInput.new_phone : undefined,
            });

            // 4. Then initiate verification
            if (emailChanged && sanitizedInput.new_email) {
                await authApi.sendEmailOtp(sanitizedInput.new_email);
                navigate('/emailverification', {
                    state: {
                        fromProfile: true,
                        new_email: sanitizedInput.new_email,
                        verifyPhone: phoneChanged,
                        new_phone: sanitizedInput.new_phone || ''
                    }
                });
                return;
            }

            if (phoneChanged && sanitizedInput.new_phone) {
                navigate('/firebase-mobile', {
                    state: {
                        fromProfile: true,
                        new_phone: '+1' + sanitizedInput.new_phone
                    }
                });
                return;
            }

            // 5. Fallback (shouldn’t happen)
            setEditingPersonal(false);
        } catch (validationError: any) {
            if (validationError.inner) {
                const fieldErrors: { [key: string]: string } = {};
                validationError.inner.forEach((err: any) => {
                    fieldErrors[err.path] = err.message;
                });
                setErrors(fieldErrors);
            } else {
                setApiError("Failed to update contact information.");
            }
        }
    };



    const handleBusinessSave = async () => {
        await settingsApi.updateBusinessInfo(businessForm);
        setEditingBusiness(false);
        handleSaveEdit()
    };

    const handleRenormalize = async () => {
        if (window.confirm("Are you sure you want to re-process all transactions? This may take a few minutes.")) {
            try {
                setIsRenormalizing(true);
                await vendorApi.renormalizeVendors();
                alert("Renormalization started successfully.");
            } catch (error) {
                alert("Failed to start renormalization.");
            } finally {
                setIsRenormalizing(false);
            }
        }
    };

    const handleSyncQbo = async () => {
        try {
            setIsSyncingQbo(true);
            await settingsApi.syncQboBusinessInfo();
            // Refresh data
            handleSaveEdit();
            alert("Business info synced from QBO successfully.");
        } catch (error) {
            alert("Failed to sync business info from QBO.");
        } finally {
            setIsSyncingQbo(false);
        }
    };

    const handleTimezoneChange = async (timezone: string) => {
        if (!activeWorkspaceId) return;
        const previousTimezone = selectedTimezone;
        setSelectedTimezone(timezone);
        try {
            setTimezoneError(null);
            setIsSavingTimezone(true);
            await workspaceApi.updateWorkspaceSettings(activeWorkspaceId, { timezone });
            refetchWorkspaceContext();
        } catch (error) {
            console.error("Failed to update workspace timezone.", error);
            setSelectedTimezone(previousTimezone);
            setTimezoneError("Failed to update workspace timezone.");
        } finally {
            setIsSavingTimezone(false);
        }
    };



    return (
        <div className="w-full font-medium">
            {/* Workspace & Business Section */}
            <div className="border rounded-[20px] p-5 mb-6">
                <div className="max-w-4xl">
                    <div className="font-semibold mb-4">Workspace</div>
                    <div className="grid grid-cols-1 gap-x-12 gap-y-5 md:grid-cols-3">
                        <div>
                            <div className="text-gray-500 text-sm mb-1">Workspace</div>
                            {workspaces.length > 1 ? (
                                <div className="relative">
                                    <select
                                        value={activeWorkspaceId || ''}
                                        onChange={(e) => setActiveWorkspaceId(e.target.value)}
                                        className="appearance-none bg-white font-medium border border-gray-200 text-gray-700 py-2 px-3 pr-8 rounded-lg leading-tight focus:outline-none focus:ring-1 focus:ring-indigo-300 cursor-pointer w-full"
                                    >
                                        {workspaces.map((ws) => (
                                            <option key={ws.workspace_id} value={ws.workspace_id}>
                                                {ws.workspace_name || ws.workspace_id}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                        <ChevronDown className="h-4 w-4" />
                                    </div>
                                </div>
                            ) : (
                                <div>{workspaces.find(ws => ws.workspace_id === activeWorkspaceId)?.workspace_name || activeWorkspaceId || '-'}</div>
                            )}
                        </div>
                        <div>
                            <div className="text-gray-500 text-sm mb-1">Business</div>
                            {businesses.length > 1 ? (
                                <div className="relative">
                                    <select
                                        value={activeBusinessId || ''}
                                        onChange={(e) => setActiveBusinessId(e.target.value)}
                                        className="appearance-none bg-white font-medium border border-gray-200 text-gray-700 py-2 px-3 pr-8 rounded-lg leading-tight focus:outline-none focus:ring-1 focus:ring-indigo-300 cursor-pointer w-full"
                                    >
                                        {businesses.map((biz) => (
                                            <option key={biz.id} value={biz.id}>
                                                {biz.legal_name || biz.dba_name || 'Unnamed Business'}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                        <ChevronDown className="h-4 w-4" />
                                    </div>
                                </div>
                            ) : (
                                <div>{businesses.find(b => b.id === activeBusinessId)?.legal_name || '-'}</div>
                            )}
                        </div>
                        <div>
                            <div className="text-gray-500 text-sm mb-1">Timezone</div>
                            <div className="relative">
                                <select
                                    value={selectedTimezone}
                                    onChange={(e) => handleTimezoneChange(e.target.value)}
                                    disabled={!activeWorkspaceId || isSavingTimezone}
                                    className="bg-white font-medium border border-gray-200 text-gray-700 py-2 px-3 rounded-lg leading-tight focus:outline-none focus:ring-1 focus:ring-indigo-300 cursor-pointer w-full disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
                                >
                                    {TIMEZONE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {isSavingTimezone && (
                                    <div className="pointer-events-none absolute inset-y-0 right-7 flex items-center text-gray-400">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    </div>
                                )}
                            </div>
                            {timezoneError && (
                                <div className="mt-1 text-xs font-normal text-red-500">
                                    {timezoneError}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Personal Details */}
            <div className="border rounded-[20px] p-5">
                <div className="max-w-xl">
                    <div className="flex justify-between items-center mb-4">
                        <div className="font-semibold flex items-center gap-2">
                            Personal Details
                        </div>
                        <div className="flex items-center gap-3">
                            {/* {!editingPersonal && (
                                <button
                                    onClick={handleRenormalize}
                                    disabled={isRenormalizing}
                                    className="text-gray-400 hover:text-indigo-600 transition-colors p-1"
                                    title="Renormalize Vendors"
                                >
                                    {isRenormalizing ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <RefreshCw className="w-4 h-4" />
                                    )}
                                </button>
                            )} */}
                            {editingPersonal ? (
                                <div className="flex gap-2 ">
                                    <button className="text-sm text-gray-500 font-medium underline" onClick={handleContactSave}>Save</button>
                                    <button className="text-sm text-gray-500 font-medium underline" onClick={() => setEditingPersonal(false)}>Cancel</button>
                                </div>
                            ) : (
                                <Pencil className="w-4 h-4 text-gray-500 cursor-pointer" onClick={() => setEditingPersonal(true)} />
                            )}
                        </div>
                    </div>

                    <div className="flex items-start gap-6">
                        <div className="w-14 h-14 rounded-full bg-gray-600 flex items-center justify-center text-lg font-semibold text-white">
                            {initials}
                        </div>

                        <div className="flex-1 grid grid-cols-2 gap-x-12 gap-y-5">
                            <div>
                                <div className="text-gray-500 text-sm">First name</div>
                                <div>{data.personal_details.first_name || '-'} </div>
                            </div>
                            <div>
                                <div className="text-gray-500 text-sm">Last name</div>
                                <div>{data.personal_details.last_name || '-'}</div>
                            </div>

                            {/* EMAIL */}
                            <div>
                                <div className="flex items-center gap-1 text-gray-500 text-sm">
                                    Email
                                    {data.personal_details.email_verified && <CircleCheck className="text-green-500 w-4 h-4" />}
                                </div>
                                {editingPersonal ? (
                                    <>
                                        <input
                                            className="input-style"
                                            value={contactForm.new_email}
                                            placeholder={data.personal_details.email || ''}
                                            onChange={(e) => setContactForm({ ...contactForm, new_email: e.target.value })}
                                        />
                                        {errors.new_email && <p className="font-normal text-sm text-red-500 mt-1">{errors.new_email}</p>}
                                        {apiError && <p className="font-normal text-sm text-red-500 mt-1">{apiError}</p>}
                                    </>
                                ) : (
                                    <div className="truncate" >{data.personal_details.email || '-'}</div>
                                )}
                            </div>

                            {/* PHONE */}
                            <div>
                                <div className="flex items-center gap-1 text-gray-500 text-sm">
                                    Mobile
                                    {data.personal_details.mobile_verified && <CircleCheck className="text-green-500 w-4 h-4" />}
                                </div>
                                {editingPersonal ? (
                                    <>
                                        <div className="relative">
                                            <input
                                                className="input-style ml-8"
                                                value={contactForm.new_phone}
                                                placeholder={data.personal_details.phone || ''}
                                                onChange={(e) => setContactForm({ ...contactForm, new_phone: e.target.value })}
                                            />
                                            <div className="pl-1 flex items-center h-full text-gray-500 absolute top-0 left-0">+1</div>
                                        </div>
                                        {errors.new_phone && <p className="font-normal text-sm text-red-500 mt-1">{errors.new_phone}</p>}
                                    </>
                                ) : (
                                    <div>{data.personal_details.phone || '-'}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Business Details */}
            <div className="border rounded-[20px] p-5 mt-6">
                <div className={`${editingBusiness ? '' : 'max-w-xl'}`}>
                    <div className="flex justify-between items-center mb-4 max-w-xl">
                        <div className="font-semibold">Business Details</div>
                        {editingBusiness ? (
                            <div className="flex gap-2 ">
                                <button className="text-sm text-gray-500 font-medium underline" onClick={handleBusinessSave}>Save</button>
                                <button className="text-sm text-gray-500 font-medium underline" onClick={() => setEditingBusiness(false)}>Cancel</button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleSyncQbo}
                                    disabled={isSyncingQbo}
                                    className="text-gray-400 hover:text-indigo-600 transition-colors p-1"
                                    title="Sync from QuickBooks"
                                >
                                    {
                                        isSyncingQbo ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <RefreshCw className="w-4 h-4" />
                                        )}
                                </button>
                                {/* <Pencil
                                    className="w-4 h-4 text-gray-500 cursor-pointer"
                                    onClick={() => {
                                        const bd = data?.business_details ?? {};
                                        setBusinessForm({
                                            business_name: bd.business_name ?? '',
                                            business_entity_name: bd.business_entity_name ?? '',
                                            employer_identification_number: bd.employer_identification_number ?? '',
                                            industry: bd.industry ?? '',
                                            business_type: bd.business_type ?? '',
                                            date_business_formed: bd.date_business_formed ?? '',
                                            street_address_1: bd.street_address_1 ?? '',
                                            street_address_2: bd.street_address_2 ?? '',
                                            city: bd.city ?? '',
                                            state: bd.state ?? '',
                                            country: bd.country ?? '',
                                            zipcode: bd.zipcode ?? '',
                                        });
                                        setEditingBusiness(true);
                                    }}
                                /> */}
                            </div>
                        )}
                    </div>

                    <div className={`${editingBusiness ? 'grid-cols-[1fr_3fr]' : 'grid-cols-2'} grid  gap-x-12 gap-y-5`}>
                        {/* Row 1 */}
                        <div>
                            <div className="text-gray-500 text-sm">Business name</div>
                            <div>{businessForm.business_name || '-'}</div>
                        </div>
                        <div className="max-w-xs">
                            <div className="text-gray-500 text-sm">Business entity name</div>
                            <div>{businessForm.business_entity_name || '-'}</div>
                        </div>

                        {/* Row 2
                        <div>
                            <div className="text-gray-500 text-sm">Employer identification number</div>
                            <div>{businessForm.employer_identification_number || '-'}</div>
                        </div> */}


                        {/* Row 3 */}
                        <div>
                            <div className="text-gray-500 text-sm">Date business was formed</div>
                            <div>{businessForm.date_business_formed || '-'}</div>
                        </div>
                        <div>
                            <div className="text-gray-500 text-sm">Business Address</div>
                            <div className="whitespace-pre-line font-medium mt-1">
                                <div>{businessForm.street_address_1 || '-'}, {businessForm.city || '-'}</div>
                                <div>{businessForm.state || '-'}, {businessForm.country || '-'}</div>
                                <div>{businessForm.zipcode || '-'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >
        </div >
    )
}
